if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('.atc-feedback__label') || this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.setAttribute('aria-busy', 'true');
        this.submitButton.classList.add('loading');
        this.submitButton.style.minWidth = `${this.submitButton.getBoundingClientRect().width}px`;
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config).then((response) => response.json());
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.resetATCFeedback();
              document.dispatchEvent(new CustomEvent('atc:feedback-error'));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            // v2.17.2 ATC feedback
            await this.showATCFeedback();
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
        } catch (e) {
          console.error(e);
            // Notify subscribers (e.g. sticky-atc) so they re-enable after a network/parse
            // failure — the response-error path publishes cartError but this catch did not,
            // leaving the sticky buy button stuck on "Adding…". (2026-06-23 QC)
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
            });
            this.resetATCFeedback();
            // v2.17.2 ATC feedback — tell the shopper the add failed, not just reset the button
            this.handleErrorMessage(window.cartStrings?.error);
            document.dispatchEvent(new CustomEvent('atc:feedback-error'));
        } finally {
            if (!this.submitButton.classList.contains('atc-feedback--success')) this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.removeAttribute('aria-busy');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
        }
      }

      // v2.17.2 ATC feedback
      async showATCFeedback() {
        const label = this.submitButton.querySelector('.atc-feedback__label');
        const liveRegion = this.querySelector('.atc-feedback__live');
        this.submitButton.classList.remove('loading');
        this.querySelector('.loading__spinner').classList.add('hidden');
        this.submitButton.classList.add('atc-feedback--success');
        if (label) label.textContent = this.submitButton.dataset.addedText || 'Added';
        if (liveRegion) liveRegion.textContent = this.submitButton.dataset.addedAnnouncement || 'Added to cart';
        document.dispatchEvent(new CustomEvent('atc:feedback-success'));
        await new Promise((resolve) => window.setTimeout(resolve, 500));
        this.resetATCFeedback();
      }

      // v2.17.2 ATC feedback
      resetATCFeedback() {
        const label = this.submitButton.querySelector('.atc-feedback__label');
        const liveRegion = this.querySelector('.atc-feedback__live');
        this.submitButton.classList.remove('loading', 'atc-feedback--success');
        this.submitButton.style.minWidth = '';
        if (liveRegion) liveRegion.textContent = '';
        if (label && !this.submitButton.hasAttribute('disabled')) {
          const price = this.submitButton.dataset.price || '';
          const addText = this.submitButton.dataset.addText || window.variantStrings.addToCart;
          label.textContent = price ? `${addText} · ${price}` : addText;
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          const price = this.submitButton.dataset.price || '';
          const addText = this.submitButton.dataset.addText || window.variantStrings.addToCart;
          this.submitButtonText.textContent = price ? addText + ' · ' + price : addText;
        }
      }

      updateSubmitPrice(price) {
        if (this.submitButton) {
          this.submitButton.dataset.price = price;
          if (!this.submitButton.hasAttribute('disabled')) {
            const addText = this.submitButton.dataset.addText || window.variantStrings.addToCart;
            this.submitButtonText.textContent = addText + ' · ' + price;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
